import { createApp } from 'vue';

export class VueAppManager {
  static apps = new Map();

  /**
   * Mount a Vue app to a DOM element
   * @param {Object} component - Vue component to mount
   * @param {string} elementId - DOM element ID to mount to
   * @param {Array} plugins - Array of Vue plugins to install
   * @param {Object} props - Props to pass to the root component
   * @returns {Object|null} - Vue app instance or null if element not found
   */
  static mountApp(component, elementId, plugins = [], props = {}) {
    try {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID '${elementId}' not found. Skipping Vue app mount.`);
        return null;
      }

      console.log(`Attempting to mount Vue app to #${elementId}`);
      
      const app = createApp(component, props);
      
      // Add global error handler
      app.config.errorHandler = (err, instance, info) => {
        console.error(`Vue error in ${elementId}:`, err);
        console.error('Component instance:', instance);
        console.error('Error info:', info);
      };
      
      // Install plugins
      plugins.forEach(plugin => {
        try {
          if (Array.isArray(plugin)) {
            app.use(plugin[0], plugin[1]); // Plugin with options
          } else {
            app.use(plugin);
          }
        } catch (pluginError) {
          console.error(`Error installing plugin for ${elementId}:`, pluginError);
        }
      });

      // Mount the app
      const mountedApp = app.mount(`#${elementId}`);
      
      // Store reference for potential cleanup
      this.apps.set(elementId, app);
      
      console.log(`✅ Vue app successfully mounted to #${elementId}`);
      return app;
    } catch (error) {
      console.error(`❌ Failed to mount Vue app to #${elementId}:`, error);
      return null;
    }
  }

  /**
   * Unmount a Vue app
   * @param {string} elementId - DOM element ID to unmount from
   */
  static unmountApp(elementId) {
    const app = this.apps.get(elementId);
    if (app) {
      app.unmount();
      this.apps.delete(elementId);
      console.log(`Vue app unmounted from #${elementId}`);
    }
  }

  /**
   * Get all mounted apps
   * @returns {Map} - Map of elementId -> app instance
   */
  static getMountedApps() {
    return this.apps;
  }
}
